import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { useAppDispatch } from 'app/store';
import { loadingActions } from 'features';
import { IAccessor, IUpdateAccessorPayload } from 'interfaces';

import { accessorApi } from '../api';
import { getApiErrorMessage, hasEquipmentSwitch } from '../utils';

type UseAccessorEditorParams = {
  accessorId: string | undefined;
};

export const useAccessorEditor = ({ accessorId }: UseAccessorEditorParams) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [accessor, setAccessor] = useState<IAccessor>();
  const [pendingValues, setPendingValues] = useState<IUpdateAccessorPayload | null>(null);
  const [confirmSwitchOpen, setConfirmSwitchOpen] = useState(false);

  const accessorNumericId = Number(accessorId);

  useEffect(() => {
    if (!Number.isFinite(accessorNumericId)) return;
    accessorApi.getById(accessorNumericId).then(setAccessor);
  }, [accessorNumericId]);

  const submitUpdate = async (values: IUpdateAccessorPayload) => {
    if (!accessor || !Number.isFinite(accessorNumericId)) return;
    dispatch(loadingActions.startLoading());
    try {
      await accessorApi.update(accessor.id, values);
      enqueueSnackbar('Accessorio modificato con successo');
      const refreshed = await accessorApi.getById(accessorNumericId);
      setAccessor(refreshed);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('pages.accessories.error-generic')), {
        variant: 'error'
      });
    } finally {
      dispatch(loadingActions.stopLoading());
    }
  };

  const persistAccessorUpdate = async (values: IUpdateAccessorPayload) => {
    if (hasEquipmentSwitch(accessor?.equipment, values?.equipment)) {
      setPendingValues(values);
      setConfirmSwitchOpen(true);
      return;
    }
    await submitUpdate(values);
  };

  const handleConfirmSwitch = async () => {
    if (!pendingValues) return;
    setConfirmSwitchOpen(false);
    const valuesToSubmit = pendingValues;
    setPendingValues(null);
    await submitUpdate(valuesToSubmit);
  };

  const handleCancelSwitch = () => {
    setConfirmSwitchOpen(false);
    setPendingValues(null);
  };

  return {
    accessor,
    confirmSwitchOpen,
    persistAccessorUpdate,
    handleConfirmSwitch,
    handleCancelSwitch
  };
};
